import { useCallback, useMemo } from 'react';
import { getRiskCommandsOrThrow } from '../orchestration/riskCommandRef';
import { RISK_INVALIDATION_CAUSES } from '../orchestration/useRiskOrchestrationCommands';

export default function useOverviewSyncControls({
    userUid,
    autoRefreshEnabled,
    pendingAutoRefresh,
    actualPortfolioIsReady,
    coreLoadVisualLoading,
    coreLoadInFlightRef,
    holdingsLoading,
    calculationsLoading,
    matricesLoading,
    positionsLoading,
    hasPortfolioData,
    overviewPriceFreshness,
    priceRefreshLoading,
    betaSourceTransitionLoading,
    viewModeLoading,
    posSizingLoading,
    pendingPosSizingAutoSync,
    posSizingSyncInFlightRef,
    posSizingTableSaving,
    posSizingAutoSyncEnabled,
    refreshPriceData,
    importApplyInFlightRef,
    priceRefreshInFlightRef,
    riskCommandsRef,
    setSnackbarMessage,
    setSnackbarSeverity,
    setSnackbarOpen,
    setPriceRefreshLoading,
    selectedFactorModel,
    normalVol,
    maxPosSize,
    aum,
    defaultTargetVolPct,
    defaultTargetVolDollar,
    defaultScaler,
    watchlistIsReady,
}) {
    const autoRefreshPending = Boolean(!autoRefreshEnabled && pendingAutoRefresh?.has_newer_prices);
    const pendingAutoRefreshCount = Number(pendingAutoRefresh?.updated_security_count_since || 0);

    const overviewSyncStatePending = (
        actualPortfolioIsReady == null ||
        coreLoadVisualLoading ||
        coreLoadInFlightRef.current ||
        holdingsLoading ||
        calculationsLoading ||
        matricesLoading ||
        positionsLoading
    );
    const noOverviewPortfolioData = (
        !overviewSyncStatePending &&
        (!hasPortfolioData || overviewPriceFreshness.isEmpty)
    );
    const hasSyncablePriceUpdate = Boolean(
        pendingAutoRefresh?.has_newer_prices || overviewPriceFreshness.staleCount > 0,
    );
    const syncPricesDisabled = (
        noOverviewPortfolioData ||
        overviewSyncStatePending ||
        priceRefreshLoading ||
        !overviewPriceFreshness.hasSnapshot ||
        !hasSyncablePriceUpdate ||
        autoRefreshEnabled
    );
    const riskAutoSyncTooltipText = autoRefreshEnabled
        ? 'Risk Auto-Sync is ON. New stored prices apply automatically to the live risk and holdings snapshot.'
        : 'Risk Auto-Sync is OFF. The live risk and holdings snapshot stays fixed until you click Sync Prices.';
    const posSizingAutoSyncToggleBusy = Boolean(
        betaSourceTransitionLoading ||
        matricesLoading ||
        viewModeLoading ||
        priceRefreshLoading ||
        posSizingLoading ||
        pendingPosSizingAutoSync ||
        coreLoadInFlightRef.current ||
        posSizingSyncInFlightRef.current
    );
    const posSizingWorkspaceBusy = Boolean(
        posSizingLoading ||
        posSizingTableSaving
    );
    const posSizingAutoSyncTooltipText = posSizingAutoSyncEnabled
        ? 'Position Sizing auto-sync is ON. Accepted parent context changes update the draft automatically.'
        : 'Position Sizing auto-sync is OFF. Accept optional context updates with Sync Draft. Portfolio changes still update the draft.';
    const posSizingAutoSyncTooltip = posSizingAutoSyncToggleBusy
        ? 'Pos-Sizing Auto-Sync is temporarily locked while external context changes are loading.'
        : posSizingAutoSyncTooltipText;

    const handlePriceRefreshRequest = useCallback(async () => {
        if (syncPricesDisabled || autoRefreshEnabled) {
            return;
        }
        await refreshPriceData({
            syncPosSizingDraft: posSizingAutoSyncEnabled,
            reason: 'risk_overview_price_refresh',
        });
    }, [
        autoRefreshEnabled,
        posSizingAutoSyncEnabled,
        refreshPriceData,
        syncPricesDisabled,
    ]);

    const handleFactorDataRefreshRequest = useCallback(async () => {
        if (!userUid) {
            return;
        }
        if (
            importApplyInFlightRef.current ||
            priceRefreshInFlightRef.current ||
            coreLoadInFlightRef.current ||
            matricesLoading
        ) {
            return;
        }

        const commands = getRiskCommandsOrThrow(
            riskCommandsRef,
            'useOverviewSyncControls.handleFactorDataRefreshRequest',
            [
                'invalidateRiskSurface',
                'refreshRiskSurface',
                'refreshPosSizingContext',
                'refreshSelectedOverview',
            ],
        );
        setPriceRefreshLoading(true);
        try {
            commands.invalidateRiskSurface({
                cause: RISK_INVALIDATION_CAUSES.FACTOR_CONTEXT_CHANGED,
                reason: 'factor_snapshot_change',
            });
            await commands.refreshRiskSurface({
                reason: 'factor_data_refresh',
                includeSelectedOverview: true,
                requireFreshMatrices: true,
            });
            await commands.refreshPosSizingContext({
                reason: 'factor_snapshot_change',
                fetchData: false,
                includeSyncStatus: true,
            });
            setSnackbarMessage('Factor data refreshed.');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Error refreshing factor data:', error);
            setSnackbarMessage('Failed to refresh factor data.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setPriceRefreshLoading(false);
            commands.refreshSelectedOverview({ reason: 'factor_snapshot_change' });
        }
    }, [
        coreLoadInFlightRef,
        importApplyInFlightRef,
        matricesLoading,
        priceRefreshInFlightRef,
        riskCommandsRef,
        setPriceRefreshLoading,
        setSnackbarMessage,
        setSnackbarOpen,
        setSnackbarSeverity,
        userUid,
    ]);

    const priceSyncControl = useMemo(() => ({
        visible: Boolean(priceRefreshLoading || (!autoRefreshEnabled && hasSyncablePriceUpdate)),
        loading: Boolean(priceRefreshLoading),
        disabled: syncPricesDisabled,
        label: priceRefreshLoading
            ? 'Syncing prices'
            : pendingAutoRefreshCount > 0
                ? `Sync ${pendingAutoRefreshCount} price ${pendingAutoRefreshCount === 1 ? 'update' : 'updates'}`
                : 'Sync prices',
        tooltip: priceRefreshLoading
            ? 'Refreshing the Risk Overview with the latest stored prices.'
            : 'Apply the latest stored prices to the Risk Overview.',
    }), [
        autoRefreshEnabled,
        hasSyncablePriceUpdate,
        pendingAutoRefreshCount,
        priceRefreshLoading,
        syncPricesDisabled,
    ]);

    const appBarUploadContext = useMemo(() => ({
        selectedFactorModel,
        serverNormalVol: normalVol,
        serverMaxPosSize: maxPosSize,
        aum,
        defaultTargetVolPct,
        defaultTargetVolDollar,
        defaultScaler,
        watchlistIsReady,
    }), [
        aum,
        defaultScaler,
        defaultTargetVolDollar,
        defaultTargetVolPct,
        maxPosSize,
        normalVol,
        selectedFactorModel,
        watchlistIsReady,
    ]);

    const appBarSyncState = useMemo(() => ({
        autoRefreshEnabled,
        posSizingAutoSyncEnabled,
        posSizingAutoSyncToggleBusy,
        autoRefreshPending,
        pendingAutoRefreshCount,
        pendingPosSizingAutoSync: Boolean(pendingPosSizingAutoSync),
        riskAutoSyncTooltipText,
        posSizingAutoSyncTooltip,
    }), [
        autoRefreshEnabled,
        autoRefreshPending,
        pendingAutoRefreshCount,
        pendingPosSizingAutoSync,
        posSizingAutoSyncEnabled,
        posSizingAutoSyncToggleBusy,
        posSizingAutoSyncTooltip,
        riskAutoSyncTooltipText,
    ]);

    return {
        posSizingAutoSyncToggleBusy,
        posSizingWorkspaceBusy,
        handlePriceRefreshRequest,
        priceSyncControl,
        handleFactorDataRefreshRequest,
        appBarUploadContext,
        appBarSyncState,
    };
}
